// Highlights missing/invalid required fields on a form so users don't have to
// hunt for them after a failed submission. Generic — usable by any form:
//   import { highlightInvalidFields } from '../helpers/form_validation'
//   if (highlightInvalidFields(form) > 0) return
// Field styles live in app/assets/stylesheets/components/_field_errors.scss.

const FIELD_CONTAINER_SELECTOR = '.form-group, .custom-field-block'
const ERROR_MESSAGE_CLASS = 'field-error-message'

// Flatpickr (with altInput) swaps the original input to type="hidden", which
// exempts it from native constraint validation — check those by value instead.
function flatpickrValueMissing(el) {
  return el.hasAttribute('required') && el._flatpickr && !el.value.trim()
}

// The phone-format mask (phone_format_controller.js) limits input to 10 US
// digits but doesn't enforce completeness — treat a started-but-short number
// as invalid. Emptiness stays the native required check's job, so optional
// phone fields left blank pass.
function phoneNumberIncomplete(el) {
  if (!el.classList || !el.classList.contains('phone-format')) return false
  const digits = el.value.replace(/^\+1\s*/, '').replace(/\D/g, '')
  return digits.length > 0 && digits.length < 10
}

function errorMessageFor(el) {
  if (phoneNumberIncomplete(el)) return 'Please enter a complete phone number'
  if (el.type === 'email' && el.validity?.typeMismatch) return 'Please enter a valid email address'
  return 'This field is required'
}

export function invalidFormFields(form) {
  return Array.from(form.elements).filter((el) =>
    (el.willValidate && !el.checkValidity()) || flatpickrValueMissing(el) || phoneNumberIncomplete(el)
  )
}

// Marks every invalid field with a red highlight and an inline message,
// scrolls the first one into view, and clears each highlight as soon as the
// user edits that field. Returns the number of invalid fields (0 when valid).
export function highlightInvalidFields(form) {
  clearInvalidFieldHighlights(form)

  const invalidFields = invalidFormFields(form)
  const containers = new Set()

  invalidFields.forEach((el) => {
    // Radio/checkbox groups produce several invalid inputs in one container —
    // dedupe so the field is only marked once.
    const container = el.closest(FIELD_CONTAINER_SELECTOR) || el.parentElement
    if (!container || containers.has(container)) return
    containers.add(container)

    const visibleControl = el._flatpickr?.altInput || el
    if (visibleControl.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]), select, textarea')) {
      visibleControl.classList.add('is-invalid')
    }
    container.classList.add('field-error')

    const message = document.createElement('div')
    message.className = ERROR_MESSAGE_CLASS
    message.textContent = errorMessageFor(el)
    // Insert after the container, not inside it — floating labels are centered
    // via top: 50% of the container, so growing it would push the label down.
    container.insertAdjacentElement('afterend', message)

    const clearHighlight = () => {
      container.classList.remove('field-error')
      message.remove()
      container.querySelectorAll('.is-invalid').forEach((node) => node.classList.remove('is-invalid'))
      container.removeEventListener('input', clearHighlight)
      container.removeEventListener('change', clearHighlight)
    }
    container.addEventListener('input', clearHighlight)
    container.addEventListener('change', clearHighlight)
  })

  const firstContainer = containers.values().next().value
  if (firstContainer) scrollToField(firstContainer)

  return invalidFields.length
}

// Scrolls the first invalid field into view. Embedded pages (iframes) get both
// mechanisms, and each is a harmless no-op where the other applies:
// - scrollIntoView handles standalone pages and raw fixed-height iframe embeds
//   (which scroll internally, and where cross-origin parents can't be scrolled).
// - postMessage handles embeds running embed.js, which auto-resizes the iframe
//   to fit its content (so it can't scroll internally) and scrolls the host
//   page instead. Hosts without embed.js simply ignore the message.
function scrollToField(container) {
  container.scrollIntoView({ behavior: 'smooth', block: 'center' })

  if (window.parent !== window) {
    const top = container.getBoundingClientRect().top + window.scrollY
    window.parent.postMessage({ sindi: 'embed', type: 'scroll-to', top: top }, '*')
  }
}

export function clearInvalidFieldHighlights(form) {
  form.querySelectorAll('.field-error').forEach((el) => el.classList.remove('field-error'))
  form.querySelectorAll(`.${ERROR_MESSAGE_CLASS}`).forEach((el) => el.remove())
  form.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'))
}
